'use client';

import Link from 'next/link';
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';
import Image from 'next/image';

export default function RentalGuides() {
  const articles = [
    {
      title: 'Complete Guide to Renting in Metro Manila',
      description: 'Everything you need to know about rental prices across Metro Manila in 2026.',
      date: 'Jan 20, 2026',
      readTime: '5 min read',
      image: '/images/placeholders/bgc-condo.jpg',
      slug: 'complete-guide-to-renting-in-metro-manila'
    },
    {
      title: 'Best Areas for Expats in Metro Manila',
      description: 'Top neighborhoods and buildings recommended for foreign professionals.',
      date: 'Jan 18, 2026',
      readTime: '7 min read',
      image: '/images/placeholders/bgc-condo-2.jpg',
      slug: 'best-areas-for-expats-in-metro-manila'
    },
    {
      title: 'Studio vs 1BR vs 2BR: Which is Right for You?',
      description: 'Comparing space, price, and lifestyle for different unit types.',
      date: 'Jan 15, 2026',
      readTime: '4 min read',
      image: '/images/placeholders/bgc-condo-3.jpg',
      slug: 'studio-vs-1br-vs-2br-comparison'
    }
  ];

  return (
    <section className="bg-gray-50 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full mb-4">
            <BookOpen className="w-4 h-4 text-[#d60500]" />
            <span className="text-sm font-semibold text-[#d60500]">Helpful Resources</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Commercial Real Estate Insights
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Expert advice on leasing and buying commercial properties in Metro Manila.
          </p>
          <Link
            href="/blogs"
            className="inline-flex items-center gap-2 text-[#d60500] font-semibold hover:gap-3 transition-all"
          >
            View all articles
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {articles.map((article, index) => (
            <Link
              key={index}
              href={`/blogs/${article.slug}`}
              className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-[#d60500] hover:shadow-xl transition-all duration-300"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden bg-gray-100">
                <Image
                  src={article.image}
                  alt={article.title}
                  fill
                  className="object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Meta */}
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {article.date}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {article.readTime}
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#d60500] transition-colors">
                  {article.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 mb-4 line-clamp-2">
                  {article.description}
                </p>

                {/* Read More */}
                <div className="flex items-center gap-2 text-[#d60500] font-semibold group-hover:gap-3 transition-all">
                  Read article
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
